import React, { useState, useRef, useEffect } from 'react';
import {
  Paperclip,
  FileText,
  Upload,
  Image,
  FileText as PdfIcon,
  File as DocIcon,
  Loader2,
  Lock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { LocalizedNumericInput } from '../../../shared/components/LocalizedNumericInput';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Separator } from '../../../components/ui/separator';
import {
  JournalEntry,
  JournalEntryCreate,
  JournalEntryLineCreate,
  JournalEntryAttachmentRef,
  Account,
  LineDimensionAssignment,
} from '../types';
import { useFile } from '../../../shared/files/hooks/useFiles';
import { fileApi } from '../../../shared/files/services/fileApi';
import type { FileMetadata } from '../../../shared/files/types';
import { FileSizeDisplay } from '../../../shared/files/components';
import { useAccounts } from '../hooks/useAccounts';
import { useDimensions } from '../hooks/useDimensions';
import { useFiscalPeriods } from '../hooks/useFiscalPeriods';
import { getErrorMessage } from '../../../shared/utils/errors';
import { AddLineButton, DeleteIconButton, DownloadIconButton, DialogForm, LtrToken, BidiText } from '../../../shared/components/common';
import { LineDimensionsCell } from './LineDimensionsCell';
import { CompanyCreateField, useCompanyCreatePrecedence } from '../../../shared/components/multi-company';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

// list-shape: embedded (the debit/credit lines of one journal entry — the entry IS the set and it must balance, so it plateaus at a handful of lines)

interface JournalEntryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: JournalEntryCreate) => Promise<void>;
  entry?: JournalEntry | null;
  /**
   * Create a NEW entry pre-filled from an existing one — the repost path after a
   * reversal, where the correction is ~95% identical to what was just reversed.
   * Mutually exclusive with `entry`: `entry` edits in place, `templateEntry` copies.
   * The copy carries lines, description, reference and attachments; its date comes
   * from `templateDate` (the reversal date) so the correction lands in the same
   * period as the reversal rather than repeating the original's date.
   */
  templateEntry?: JournalEntry | null;
  templateDate?: string;
  isLoading?: boolean;
}

interface LineItem {
  id: string;
  account_id: string;
  description: string;
  debit_amount: number | '';
  credit_amount: number | '';
  dimensions: LineDimensionAssignment[];
}

interface PendingAttachment {
  id: string;
  file: File;
  uploading: boolean;
  error?: string;
  uploadedFile?: FileMetadata;
}

function generateId(): string {
  return Math.random().toString(36).substr(2, 9);
}

function getFileIcon(mimeType: string) {
  // not-a-renderer: picks an ICON, never an <img> — an image glyph is still the
  // right label for a TIFF attachment.
  if (mimeType.startsWith('image/')) return <Image className="h-5 w-5 text-primary" />;
  if (mimeType === 'application/pdf') return <PdfIcon className="h-5 w-5 text-destructive" />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <DocIcon className="h-5 w-5 text-info" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
}

/**
 * A persisted attachment on a journal entry. The JE response carries only the
 * `file_id` (see journal_entry.schema.json — display metadata is never denormalized
 * onto the entry), so the row resolves name / size / type live from file_management.
 */
function ExistingAttachmentRow({
  fileId,
  onDownload,
  onRemove,
  downloadTooltip,
  removeTooltip,
}: {
  fileId: string;
  onDownload: (file: Pick<FileMetadata, 'id' | 'original_filename' | 'filename' | 'access_mode'>) => void;
  onRemove: (fileId: string) => void;
  downloadTooltip: string;
  removeTooltip: string;
}) {
  const { data } = useFile(fileId);
  const file = data?.data;
  return (
    <div className="flex items-center gap-3 p-2 rounded border">
      {getFileIcon(file?.mime_type ?? '')}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          <BidiText>{file?.original_filename ?? file?.filename ?? ''}</BidiText>
        </p>
        {typeof file?.file_size === 'number' && (
          <FileSizeDisplay bytes={file.file_size} className="block text-xs text-muted-foreground" />
        )}
      </div>
      <div className="flex items-center gap-1">
        <DownloadIconButton
          size="sm"
          onClick={() => file && onDownload(file)}
          tooltip={downloadTooltip}
        />
        <DeleteIconButton size="sm" onClick={() => onRemove(fileId)} tooltip={removeTooltip} />
      </div>
    </div>
  );
}

const MAX_FILE_SIZE_MB = 50;

/** Upper bound for the period lookup below — 500 monthly periods is >40 years. */
const FISCAL_PERIOD_LOOKUP_LIMIT = 500;

/** A fiscal period's start/end as the plain `YYYY-MM-DD` the date input speaks. */
function dateOnly(value: string): string {
  return value.slice(0, 10);
}

export function JournalEntryForm({
  open,
  onClose,
  onSubmit,
  entry,
  templateEntry,
  templateDate,
  isLoading,
}: JournalEntryFormProps) {
  const { t } = useTranslation('accounting');
  const { data: accountsData } = useAccounts({ is_active: true, is_header: false });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const accounts: Account[] = accountsData && 'data' in accountsData
    ? (accountsData.data as Account[])
    : [];

  // Applicable analytic axes for journal-entry lines (active + applies_to_je).
  const { data: dimensionsData } = useDimensions({ applies_to: 'je' });
  const jeDimensions = (dimensionsData?.data ?? []).filter((d) => d.is_active);
  const showDimensions = jeDimensions.length > 0;

  const companyPrecedence = useCompanyCreatePrecedence();
  const { multiCompany, defaultCompanyId } = companyPrecedence;
  const isEditing = !!entry;
  const isCopy = !entry && !!templateEntry;
  // The entry whose content pre-fills the form: the one being edited, or the one
  // being copied for a repost. Only `entry` makes this an in-place edit.
  const source = entry ?? templateEntry ?? null;
  const initialDate = entry?.entry_date
    ?? (isCopy ? templateDate ?? templateEntry?.entry_date : undefined)
    ?? toDateOnlyString(new Date());
  const [companyId, setCompanyId] = useState('');
  const [entryDate, setEntryDate] = useState(initialDate);
  const [description, setDescription] = useState(source?.description || '');
  const [reference, setReference] = useState(source?.reference || '');
  const [lines, setLines] = useState<LineItem[]>(() => {
    if (source?.lines && source.lines.length > 0) {
      return source.lines.map((line) => ({
        id: generateId(),
        account_id: line.account_id,
        description: line.description || '',
        debit_amount: line.debit_amount || '',
        credit_amount: line.credit_amount || '',
        dimensions: line.dimensions || [],
      }));
    }
    return [
      { id: generateId(), account_id: '', description: '', debit_amount: '', credit_amount: '', dimensions: [] },
      { id: generateId(), account_id: '', description: '', debit_amount: '', credit_amount: '', dimensions: [] },
    ];
  });

  // Account combobox state per line
  const [openCombobox, setOpenCombobox] = useState<string | null>(null);

  // Attachments state
  const [existingAttachments, setExistingAttachments] = useState<JournalEntryAttachmentRef[]>(
    source?.attachments || []
  );
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Why the last save was refused, translated by error code. The dialog stays
  // open on failure, so a transient toast alone left the form looking as if
  // nothing had happened (ACC-J7).
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The fiscal period covering the chosen date, when it is no longer open. The
  // backend refuses to post into a closed/locked period; surfacing it here means
  // the boundary is visible while the user is still choosing the date instead of
  // only after a rejected save. Periods are one small row per month.
  const { data: fiscalPeriodsData } = useFiscalPeriods(
    { company_id: companyId || undefined, limit: FISCAL_PERIOD_LOOKUP_LIMIT },
    { enabled: open }
  );
  const blockedPeriod = (fiscalPeriodsData?.data ?? []).find(
    (period) =>
      period.status !== 'open' &&
      dateOnly(period.start_date) <= entryDate &&
      entryDate <= dateOnly(period.end_date)
  );

  // Reset form state when dialog opens/closes or entry changes
  useEffect(() => {
    if (open) {
      // Company precedence (create only); editing locks company server-side.
      // A copy posts into the company that owns the entry it was copied from —
      // re-picking it would let a correction land on the wrong legal entity.
      setCompanyId(isEditing ? '' : (isCopy ? templateEntry.company_id : defaultCompanyId));
      setEntryDate(initialDate);
      setDescription(source?.description || '');
      setReference(source?.reference || '');
      setLines(
        source?.lines && source.lines.length > 0
          ? source.lines.map((line) => ({
              id: generateId(),
              account_id: line.account_id,
              description: line.description || '',
              debit_amount: line.debit_amount || '',
              credit_amount: line.credit_amount || '',
              dimensions: line.dimensions || [],
            }))
          : [
              { id: generateId(), account_id: '', description: '', debit_amount: '', credit_amount: '', dimensions: [] },
              { id: generateId(), account_id: '', description: '', debit_amount: '', credit_amount: '', dimensions: [] },
            ]
      );
      setExistingAttachments(source?.attachments || []);
      setPendingAttachments([]);
      setUploadError(null);
      setSubmitError(null);
    }
  }, [open, source, isEditing, isCopy, templateEntry, initialDate, defaultCompanyId]);

  const totalDebits = lines.reduce(
    (sum, line) => sum + (typeof line.debit_amount === 'number' ? line.debit_amount : 0),
    0
  );
  const totalCredits = lines.reduce(
    (sum, line) => sum + (typeof line.credit_amount === 'number' ? line.credit_amount : 0),
    0
  );
  const difference = totalDebits - totalCredits;
  const isBalanced = Math.abs(difference) < 0.01;

  const addLine = () => {
    setLines([...lines, { id: generateId(), account_id: '', description: '', debit_amount: '', credit_amount: '', dimensions: [] }]);
  };

  const removeLine = (id: string) => {
    if (lines.length > 2) {
      setLines(lines.filter((line) => line.id !== id));
    }
  };

  const updateLine = (id: string, field: keyof LineItem, value: string | number) => {
    setLines(
      lines.map((line) => {
        if (line.id === id) {
          const updated = { ...line, [field]: value };
          // Clear opposite amount when entering a value
          if (field === 'debit_amount' && value) {
            updated.credit_amount = '';
          } else if (field === 'credit_amount' && value) {
            updated.debit_amount = '';
          }
          return updated;
        }
        return line;
      })
    );
  };

  const updateLineDimensions = (id: string, dimensions: LineDimensionAssignment[]) => {
    setLines(lines.map((line) => (line.id === id ? { ...line, dimensions } : line)));
  };

  // File handling
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setUploadError(null);

    for (const file of Array.from(files)) {
      // Check file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setUploadError(t('journalEntries.fileTooLarge', { size: MAX_FILE_SIZE_MB }));
        continue;
      }

      const pendingId = generateId();
      const pending: PendingAttachment = {
        id: pendingId,
        file,
        uploading: false,
      };

      setPendingAttachments((prev) => [...prev, pending]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePendingAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((p) => p.id !== id));
  };

  const handleRemoveExistingAttachment = (fileId: string) => {
    setExistingAttachments((prev) => prev.filter((a) => a.file_id !== fileId));
  };

  // Download through the shared file seam: it fetches with a bearer token (the raw
  // /download endpoint is authenticated, so window.open'ing it 401'd — it never
  // carried the Authorization header) and follows the presigned redirect on S3.
  const handleDownload = (file: Pick<FileMetadata, 'id' | 'original_filename' | 'filename' | 'access_mode'>) => {
    void fileApi.download(file.id, file.original_filename || file.filename, file.access_mode);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const event = {
        target: { files },
      } as React.ChangeEvent<HTMLInputElement>;
      handleFileSelect(event);
    }
  };

  const handleSubmit = async () => {
    const entryLines: JournalEntryLineCreate[] = lines
      .filter((line) => line.account_id && (line.debit_amount || line.credit_amount))
      .map((line) => ({
        account_id: line.account_id,
        description: line.description || undefined,
        // Amounts are the lossless-string wire type (DECIMAL(18,4), same convention
        // as exchange_rate) — send the exact digits as a string so the value is not
        // floated at the JSON boundary. The local `number` state above still drives
        // the balance-check arithmetic; only the serialized payload is stringified.
        debit_amount: typeof line.debit_amount === 'number' ? String(line.debit_amount) : undefined,
        credit_amount: typeof line.credit_amount === 'number' ? String(line.credit_amount) : undefined,
        dimensions: line.dimensions.length > 0 ? line.dimensions : undefined,
      }));

    const attachmentIds = [
      ...existingAttachments.map((a) => a.file_id),
      ...pendingAttachments
        .filter((p) => p.uploadedFile)
        .map((p) => p.uploadedFile!.id),
    ];

    // Multi-company guard on create — when editing, company is locked by the
    // backend (posted entries cannot change company), so we don't enforce here.
    // Rule 2's acknowledgement rides along, and only on a blank create: a copy
    // inherits the source entry's company (rule 1) and an edit renders no field
    // at all, so neither has a control to answer a prompt with.
    if (!isEditing && !isCopy && companyPrecedence.blockCreate(companyId)) return;
    if (!isEditing && multiCompany && !companyId) return;

    const data: JournalEntryCreate = {
      entry_date: entryDate,
      description,
      reference: reference || undefined,
      lines: entryLines,
      attachments: attachmentIds.map((id) => ({ file_id: id })),
      ...(isEditing ? {} : { company_id: companyId || undefined }),
    };

    setSubmitError(null);
    try {
      await onSubmit(data);
    } catch (error) {
      // Translated by error code — FISCAL_PERIOD_CLOSED names the period. The
      // dialog keeps the user's work and says why the save was refused.
      setSubmitError(getErrorMessage(error));
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const hasAnyUploading = pendingAttachments.some((p) => p.uploading);
  const needsCompanyPick = !isEditing && multiCompany && !companyId;
  const canSubmit = !isLoading && isBalanced && lines.filter((l) => l.account_id).length >= 2 && !hasAnyUploading && !needsCompanyPick && !blockedPeriod;

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={
        isEditing
          ? t('journalEntries.editEntry')
          : isCopy
            ? t('journalEntries.correctEntryTitle', { number: templateEntry.entry_number })
            : t('journalEntries.createEntry')
      }
      onSubmit={handleSubmit}
      submitLabel={entry ? t('common:app.save') : t('common:app.create')}
      isLoading={isLoading}
      maxWidth="lg"
      isDisabled={!canSubmit}
    >
      {isCopy && (
        <Alert className="mt-2">
          <AlertDescription>
            {t('journalEntries.correctEntryHint', { number: templateEntry.entry_number })}
          </AlertDescription>
        </Alert>
      )}

      {/* Company is pickable only on a blank create — a copy inherits the source
          entry's legal entity, and an edit locks it server-side. */}
      {!isEditing && !isCopy && (
        <div className="mt-2">
          <CompanyCreateField
            precedence={companyPrecedence}
            value={companyId}
            onChange={setCompanyId}
          />
        </div>
      )}

      {/* Attachments Section */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-2 text-sm font-medium">
          <Paperclip className="h-4 w-4" />
          {t('journalEntries.attachments')}
        </div>

        {/* Drop zone */}
        <div
          className={cn(
            'p-6 text-center cursor-pointer rounded-lg border-2 border-dashed',
            'bg-muted/50 hover:bg-muted hover:border-primary transition-colors'
          )}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFileSelect}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          />
          <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">
            {t('journalEntries.dragDropHint')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('journalEntries.maxFileSize', { size: MAX_FILE_SIZE_MB })}
          </p>
        </div>

        {uploadError && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{uploadError}</AlertDescription>
          </Alert>
        )}

        {/* Attachment list */}
        {(existingAttachments.length > 0 || pendingAttachments.length > 0) && (
          <div className="mt-2 space-y-1">
            {existingAttachments.map((attachment) => (
              <ExistingAttachmentRow
                key={attachment.file_id}
                fileId={attachment.file_id}
                onDownload={handleDownload}
                onRemove={handleRemoveExistingAttachment}
                downloadTooltip={t('journalEntries.downloadAttachment')}
                removeTooltip={t('journalEntries.removeAttachment')}
              />
            ))}

            {existingAttachments.length > 0 && pendingAttachments.length > 0 && (
              <Separator />
            )}

            {pendingAttachments.map((pending) => (
              <div key={pending.id} className="flex items-center gap-3 p-2 rounded border">
                {pending.uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : pending.error ? (
                  <FileText className="h-5 w-5 text-destructive" />
                ) : (
                  getFileIcon(pending.file.type)
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{pending.file.name}</p>
                  <p className={cn(
                    'text-xs',
                    pending.error ? 'text-destructive' : 'text-muted-foreground'
                  )}>
                    {pending.error || (pending.uploading
                      ? t('journalEntries.parsingDocument')
                      : <FileSizeDisplay bytes={pending.file.size} />)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {pending.uploadedFile && (
                    <DownloadIconButton
                      size="sm"
                      onClick={() => handleDownload(pending.uploadedFile!)}
                      tooltip={t('journalEntries.downloadAttachment')}
                    />
                  )}
                  <DeleteIconButton
                    size="sm"
                    onClick={() => handleRemovePendingAttachment(pending.id)}
                    disabled={pending.uploading}
                    tooltip={t('journalEntries.removeAttachment')}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Entry details */}
      <div className="grid grid-cols-12 gap-4 mt-6">
        <div className="col-span-4 space-y-2">
          <Label>{t('journalEntries.entryDate')}</Label>
          <Input
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            aria-invalid={!!blockedPeriod}
            aria-describedby={blockedPeriod ? 'je-closed-period-warning' : undefined}
          />
        </div>
        {blockedPeriod && (
          <div className="col-span-12">
            <Alert id="je-closed-period-warning" variant="warning">
              <Lock className="h-4 w-4" />
              <AlertDescription>
                {/* The period name is a standalone token ("2026-06", "March 2026") —
                    pinned LTR with LtrToken so it can't scramble under an RTL locale. */}
                <LtrToken>{blockedPeriod.name}</LtrToken>{' '}
                {t('journalEntries.closedPeriodWarning')}{' '}
                <Link
                  to="/accounting/setup?section=fiscal-periods"
                  className="font-medium underline underline-offset-2"
                >
                  {t('journalEntries.openFiscalPeriods')}
                </Link>
              </AlertDescription>
            </Alert>
          </div>
        )}
        <div className="col-span-4 space-y-2">
          <Label>{t('journalEntries.reference')}</Label>
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
        <div className="col-span-4 space-y-2">
          {entry?.entry_number && (
            <>
              <Label>{t('journalEntries.entryNumber')}</Label>
              <Input value={entry.entry_number} disabled />
            </>
          )}
        </div>
        <div className="col-span-12 space-y-2">
          <Label>{t('journalEntries.description')}</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>
      </div>

      <div className="mt-6">
        <p className="text-sm font-medium mb-2">{t('journalEntries.lines')}</p>
        <div className="overflow-x-auto">
        <Table className={showDimensions ? 'min-w-[760px]' : 'min-w-[640px]'}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[30%]">{t('journalEntries.account')}</TableHead>
              <TableHead className="w-[20%]">{t('journalEntries.lineDescription')}</TableHead>
              <TableHead className="w-[15%] text-right">{t('journalEntries.debit')}</TableHead>
              <TableHead className="w-[15%] text-right">{t('journalEntries.credit')}</TableHead>
              {showDimensions && (
                <TableHead className="w-[15%]">{t('dimensions.lineAssignment')}</TableHead>
              )}
              <TableHead className="w-[5%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>
                  <Popover open={openCombobox === line.id} onOpenChange={(open) => setOpenCombobox(open ? line.id : null)}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full justify-start font-normal">
                        {line.account_id
                          ? accounts.find((a) => a.id === line.account_id)
                            ? <><LtrToken>{accounts.find((a) => a.id === line.account_id)?.account_number}</LtrToken> - <BidiText>{accounts.find((a) => a.id === line.account_id)?.name}</BidiText></>
                            : t('journalEntries.selectAccount')
                          : t('journalEntries.selectAccount')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder={t('journalEntries.searchAccount')} />
                        <CommandList>
                          <CommandEmpty>{t('journalEntries.noAccountFound')}</CommandEmpty>
                          <CommandGroup>
                            {accounts.map((account) => (
                              <CommandItem
                                key={account.id}
                                value={`${account.account_number} ${account.name}`}
                                onSelect={() => {
                                  updateLine(line.id, 'account_id', account.id);
                                  setOpenCombobox(null);
                                }}
                              >
                                <LtrToken>{account.account_number}</LtrToken> - <BidiText>{account.name}</BidiText>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </TableCell>
                <TableCell>
                  <Input
                    size="sm"
                    value={line.description}
                    onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <LocalizedNumericInput
                    size="sm"
                    value={typeof line.debit_amount === 'number' ? line.debit_amount : null}
                    onChange={(v) =>
                      updateLine(
                        line.id,
                        'debit_amount',
                        v !== null ? v : ''
                      )
                    }
                    min={0}
                    decimalPlaces={2}
                  />
                </TableCell>
                <TableCell>
                  <LocalizedNumericInput
                    size="sm"
                    value={typeof line.credit_amount === 'number' ? line.credit_amount : null}
                    onChange={(v) =>
                      updateLine(
                        line.id,
                        'credit_amount',
                        v !== null ? v : ''
                      )
                    }
                    min={0}
                    decimalPlaces={2}
                  />
                </TableCell>
                {showDimensions && (
                  <TableCell>
                    <LineDimensionsCell
                      dimensions={jeDimensions}
                      value={line.dimensions}
                      onChange={(dims) => updateLineDimensions(line.id, dims)}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <DeleteIconButton
                    onClick={() => removeLine(line.id)}
                    disabled={lines.length <= 2}
                    tooltip={t('common:app.remove')}
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={showDimensions ? 6 : 5} className="p-2">
                <AddLineButton label={t('common:app.addLine')} onClick={addLine} />
              </TableCell>
            </TableRow>
            <TableRow className="bg-muted/50">
              <TableCell colSpan={2}>
                <span className="font-medium">{t('journalEntries.totals')}</span>
              </TableCell>
              <TableCell className="text-right">
                <span className="font-medium">{formatCurrency(totalDebits)}</span>
              </TableCell>
              <TableCell className="text-right">
                <span className="font-medium">{formatCurrency(totalCredits)}</span>
              </TableCell>
              {showDimensions && <TableCell />}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
        </div>

        {!isBalanced && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              {t('journalEntries.outOfBalance', {
                difference: formatCurrency(Math.abs(difference)),
                side: difference > 0 ? t('journalEntries.debit') : t('journalEntries.credit'),
              })}
            </AlertDescription>
          </Alert>
        )}

        {submitError && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}
      </div>
    </DialogForm>
  );
}
